<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { required, helpers } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'
import DatePicker from '@/components/Applications/Shared/DatePicker.vue'

const { t } = useI18n()
const { umsokn } = useUmsokn()

const props = defineProps<{
  disable?: boolean
}>()

const rules = {
  umsokn: {
    skattThrep: { required },
    hlutfallPersonuAfslattar: { required },
    // If start date is required in your flow, uncomment:
    // fraDags: { required }
  }
}

const v$ = useVuelidate(rules, { umsokn })

function onPercentInput(v: any) {
  const digits = String(v ?? '').replace(/\D/g, '')
  const clamped = digits === '' ? '' : String(Math.min(100, Number(digits)))
  if (umsokn.value) umsokn.value.hlutfallPersonuAfslattar = clamped as any
}

/** Called by ApplicationBase before navigating forward */
async function validate(): Promise<boolean> {
  await v$.value.$validate()
  return !v$.value.$error
}

defineExpose({ validate })
</script>

<template>
  <div v-if="umsokn">
    <v-col>
      <v-row>
        <v-col>
          <h2 class="forms__header">
            {{ t('utgreidsla_umsokn_stadgreidslaskatta_header') }}
          </h2>
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <p style="font-weight:bold">
            {{ t('utgreidsla_umsokn_stadgreidslaskatta_header2') }}
          </p>
        </v-col>
      </v-row>

      <!-- Skattþrep (values are translated strings to satisfy backend) -->
      <v-row>
        <v-col cols="12">
          <v-radio-group
            v-model="umsokn.skattThrep"
            :readonly="props.disable"
            hide-details
            :error="v$.umsokn.skattThrep.$error"
            :error-messages="v$.umsokn.skattThrep.$error ? [t('utgreidsla_umsokn_stadgreidslaskatta_threp_error')] : []"
          >
            <v-radio
              :label="t('utgreidsla_umsokn_stadgreidslaskatta_threp1')"
              :value="t('utgreidsla_umsokn_stadgreidslaskatta_threp1')"
            />
            <v-radio
              :label="t('utgreidsla_umsokn_stadgreidslaskatta_threp2')"
              :value="t('utgreidsla_umsokn_stadgreidslaskatta_threp2')"
            />
            <v-radio
              :label="t('utgreidsla_umsokn_stadgreidslaskatta_threp3')"
              :value="t('utgreidsla_umsokn_stadgreidslaskatta_threp3')"
            />
          </v-radio-group>
        </v-col>
      </v-row>

      <!-- Hlutfall + Dagsetning -->
      <v-row class="m-t-20">
        <v-col cols="12" md="6">
          <v-text-field
            v-model="umsokn.hlutfallPersonuAfslattar"
            :label="t('utgreidsla_umsokn_stadgreidslaskatta_hlutfall')"
            variant="outlined"
            density="comfortable"
            :readonly="props.disable"
            inputmode="numeric"
            type="tel"
            suffix="%"
            @update:model-value="onPercentInput"
            :error="v$.umsokn.hlutfallPersonuAfslattar.$error"
            :error-messages="v$.umsokn.hlutfallPersonuAfslattar.$error ? [t('umsokn_general_required_error') || t('common.val_invalid')] : []"
          />
        </v-col>

        <v-col cols="12" md="6">
          <DatePicker
            v-model="umsokn.fraDags"
            :disabled="props.disable"
            :label="t('lifeyri_umsokn_step3_date_label')"
            :error="v$.umsokn.fraDags?.$error"
            :error-messages="(v$.umsokn.fraDags?.$errors || []).map((e: any) => e.$message)"
            :first-day-of-week="1"
            placeholder="DD.MM.YYYY"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <p style="font-size:14px">
            {{ t('utgreidsla_umsokn_stadgreidslaskatta_hlutfall_skyring') }}
          </p>
        </v-col>
      </v-row>
    </v-col>
  </div>
</template>

<style scoped>
.m-b-40 { margin-bottom: 40px; }
.forms__header {
  color: #194458;
  font-weight: 900;
  font-size: 28px;
  line-height: 1.25;
  margin: 0 0 12px;
}
</style>
